/**
 * A set's list swaps in place for one item's expanded view, and back.
 *
 * This is the list/detail duality of the data made physical: the cheap list
 * call returns many rows of few fields, and picking one is exactly the moment
 * a reader wants the fields that row had no room for. Making that a navigation
 * would throw away the set they were reading.
 *
 * Only the pane animates; the frame takes the INCOMING content's natural
 * height, which avoids a layout transition and keeps the interaction
 * responsive. Forward arrives from the trailing edge and back from the leading
 * edge, on the INLINE axis, so a right-to-left surface does not get its
 * direction of travel reversed.
 *
 * The outgoing pane stays mounted for the length of the move, out of flow and
 * `inert`, and leaves in the mirror of the arrival. Unmounting it on the first
 * frame is what made a retraction look nothing like the expansion it undid —
 * and, because the item's one control lives in the list, it is also what threw
 * focus to `body` the instant a reader opened anything.
 *
 * The clip is a TRANSITION device, and it is held only for the length of a
 * move. Two panes sliding past each other need an edge to slide past; a pane at
 * rest does not, and a permanent clip is what cut the corner off everything a
 * settled pane deliberately hangs outside its own box — a pinned chart tooltip
 * at the end of its axis being the case that found it. The formatting context
 * is pinned with `flow-root` so only the clipping toggles: releasing a block
 * formatting context would let a pane's first margin collapse out through the
 * frame and move the whole drill.
 */
"use client";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import { IconChevronLeft } from "@tabler/icons-react";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import { CRC_D_MOVE } from "./cresultcard.motion";
import {
  CRC_R_TILE,
  CRC_TEXT,
  CRC_TOUCH_TARGET,
} from "./cresultcard.tokens";
import { useCresultcardViewport } from "./cresultcard.viewport";
import {
  CresultcardDrillDirection,
  CresultcardDrillPhase,
} from "./cresultcard.types";

interface CresultcardDrillProps {
  readonly list: ReactNode;
  /** Null while the list is showing. */
  readonly expanded: ReactNode | null;
  /** Changes whenever the expanded item changes, so a row→row jump re-animates. */
  readonly expandedKey: string | null;
}

const LIST_KEY = "list";
const PANE_CLASS_NAME = "cresultcard__drill-pane";

/**
 * A block formatting context either way, so the toggle changes what is painted
 * outside the frame and nothing about how the panes inside it are laid out.
 */
const DRILL_DISPLAY = "flow-root";

/** Whether the frame is holding an edge for a pane to travel past. */
enum DrillClip {
  Moving = "hidden",
  Rest = "visible",
}

/** The pane on its way out is out of flow, so the frame sizes to the arrival. */
const EXIT_PANE_STYLE: CSSProperties = {
  position: "absolute",
  insetBlockStart: 0,
  insetInline: 0,
};

interface DrillPaneContent {
  readonly key: string;
  readonly node: ReactNode;
  readonly direction: CresultcardDrillDirection;
}

export function CresultcardDrill({
  list,
  expanded,
  expandedKey,
}: CresultcardDrillProps): ReactElement {
  const showingExpanded = expanded !== null;
  const direction = showingExpanded
    ? CresultcardDrillDirection.Forward
    : CresultcardDrillDirection.Back;
  const currentKey = expandedKey ?? LIST_KEY;
  const current = expanded ?? list;
  // What is on screen right now, so the NEXT swap knows what it is replacing.
  // A ref rather than state: this is a record of the last commit, and reading
  // it during render would make the exit depend on when React chose to re-run.
  const settledRef = useRef<Omit<DrillPaneContent, "direction">>({
    key: currentKey,
    node: current,
  });
  const [exiting, setExiting] = useState<DrillPaneContent | null>(null);
  // The arriving pane animates on mount too, so the frame starts clipped.
  const [moving, setMoving] = useState<boolean>(true);

  useLayoutEffect((): void => {
    const settled = settledRef.current;
    settledRef.current = { key: currentKey, node: current };
    if (settled.key === currentKey) {
      return;
    }
    setExiting({ direction, key: settled.key, node: settled.node });
    // Before paint, not after: an effect that runs once the arriving pane has
    // already been drawn re-clips a frame late, and that one frame is the whole
    // travel distance of the pane's first step.
    setMoving(true);
  }, [current, currentKey, direction]);

  // The retirement runs on its OWN clock, keyed by what is leaving. Arming it
  // inside the swap effect above looked equivalent and was not: that effect
  // re-runs on every render (its `current` is a fresh element each time), so the
  // first re-render tore the timer down and the early return never re-armed it —
  // and the pane on its way out stayed on screen for good.
  const exitingKey = exiting?.key ?? null;
  useEffect((): (() => void) | undefined => {
    if (exitingKey == null) {
      return undefined;
    }
    const timer = window.setTimeout((): void => {
      setExiting(null);
    }, CRC_D_MOVE);
    return (): void => {
      window.clearTimeout(timer);
    };
  }, [exitingKey]);

  // The clip is released on the same duration as that retirement, but keyed by
  // what is ARRIVING rather than by what is leaving, so the first move — the
  // mount, which retires nothing — is timed like every other one. It is armed
  // after the retirement so the frame is never released while a pane it was
  // holding is still mounted.
  useEffect((): (() => void) => {
    const timer = window.setTimeout((): void => {
      setMoving(false);
    }, CRC_D_MOVE);
    return (): void => {
      window.clearTimeout(timer);
    };
  }, [currentKey]);

  // A swap back inside the exit window — open then close again before the move
  // has finished — briefly leaves the pane on its way out holding the key of
  // the pane arriving, and two siblings under one key is a React error and a
  // reconciliation the drill cannot survive. The retirement is simply already
  // over in that case: what was leaving is what came back.
  const exitPane = exiting != null && exiting.key !== currentKey ? exiting : null;

  return (
    <div
      data-el="Cresultcard/Drill/Root"
      style={{
        position: "relative",
        display: DRILL_DISPLAY,
        minWidth: 0,
        overflow: moving ? DrillClip.Moving : DrillClip.Rest,
      }}
    >
      {exitPane != null && (
        <div
          className={PANE_CLASS_NAME}
          data-el="Cresultcard/Drill/ExitPanel"
          data-crc-direction={exitPane.direction}
          data-crc-phase={CresultcardDrillPhase.Exit}
          inert
          key={exitPane.key}
          style={EXIT_PANE_STYLE}
        >
          {exitPane.node}
        </div>
      )}
      <div
        className={PANE_CLASS_NAME}
        data-el="Cresultcard/Drill/EntryPanel"
        data-crc-direction={direction}
        data-crc-phase={CresultcardDrillPhase.Enter}
        key={currentKey}
      >
        {current}
      </div>
    </div>
  );
}

const BACK_LABEL_PREFIX = "Back to ";
const BACK_GLYPH_SIZE = 12;
const BACK_GLYPH_STROKE = 2;
const BACK_SIZE_WIDE = 26;
const BACK_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/DrillBack/Root",
  "data-crc-control": true,
};

interface CresultcardDrillBackProps {
  /** The set's own label, so the way out names where it goes. */
  readonly label: string;
  /** The pane this control collapses, so the open state has a carrier. */
  readonly paneId: string;
  readonly onBack: () => void;
}

/**
 * The way out. It takes the header's LEADING slot, replacing the set mark
 * rather than sitting somewhere new, so opening an item never adds a row to
 * the header and the widget stays exactly as tall as its content needs.
 *
 * It also carries the pane's expanded state. The title button owns
 * `aria-expanded` while the item is closed, but the drill REPLACES the list, so
 * that control is not on the surface once the pane is open — and a pane nothing
 * declares as expanded reads to assistive tech as a page that simply changed.
 */
export function CresultcardDrillBack({
  label,
  paneId,
  onBack,
}: CresultcardDrillBackProps): ReactElement {
  const { isMobile } = useCresultcardViewport();
  const size = isMobile ? CRC_TOUCH_TARGET : BACK_SIZE_WIDE;

  return (
    <Cbutton
      ariaLabel={`${BACK_LABEL_PREFIX}${label}`}
      containerStyle={{
        width: size,
        height: size,
        color: CRC_TEXT,
        backgroundColor: "rgba(255,255,255,0.07)",
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.1)",
      }}
      disableClickMotion
      onClick={onBack}
      padding={0}
      preset={ButtonPreset.Ghost}
      radius={CRC_R_TILE}
      rootAttributes={{
        ...BACK_ROOT_ATTRIBUTES,
        "aria-controls": paneId,
        "aria-expanded": true,
      }}
      style={{ flexShrink: 0 }}
    >
      <IconChevronLeft size={BACK_GLYPH_SIZE} stroke={BACK_GLYPH_STROKE} />
    </Cbutton>
  );
}
